import { Button, Select } from 'antd';
// eslint-disable-next-line import/no-extraneous-dependencies
import type { DefaultOptionType } from 'rc-select/lib/Select';
import { useCallback, useMemo, useRef, useState } from 'react';

import './SpecificationIntervalMultiselect.scss';

import type { InputStatus } from 'antd/lib/_util/statusUtils';
import {
  areMeasureUnitIntervalValueAndOptionEqual,
  toCustomIntervalDefaultType,
  toIntervalDefaultType,
  toIntervalOptionValue,
  toIntervalValue,
} from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationInterval.functions';
import type {
  IntervalOptionValue,
  IntervalValue,
  MeasureUnitIntervalValue,
} from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationIntervalField.types';
import {
  SpecificationIntervalPopup,
  type SpecificationIntervalPopupRef,
} from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationIntervalPopup';
import { cNs } from 'src/helpers';
import type { IProductSpecificationFieldReference } from 'src/types/reference/IProductSpecificationReference';
import {
  SPECIFICATION_MEASURE_UNIT_MAPPER,
  SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER,
  SpecificationMeasureUnit,
} from 'src/types/reference/SpecificationMeasureUnit';

function OptionRender({ value }: { value?: string | number }) {
  return `${SPECIFICATION_MEASURE_UNIT_MAPPER[value as SpecificationMeasureUnit]} (${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value as SpecificationMeasureUnit]})`;
}

type SpecificationIntervalMultiselectProps = {
  field: IProductSpecificationFieldReference;

  defaultValue?: IntervalValue;

  onChange?: (selectedValue: IntervalValue) => void;

  status?: InputStatus;
  disabled?: boolean;

  containerClassName?: string;
  className?: string;
};

export function SpecificationIntervalMultiselect({
  field,
  defaultValue,
  onChange,

  status,
  disabled,

  containerClassName,
  className,
}: Readonly<SpecificationIntervalMultiselectProps>) {
  const specificationMultiselectCascaderPopupRef = useRef<SpecificationIntervalPopupRef>(null);

  const noMeasureUnitsAllowed =
    field.allowedMeasureUnits.length === 1 && field.allowedMeasureUnits[0] === SpecificationMeasureUnit.NONE;

  const [selectedMeasureUnit, setSelectedMeasureUnit] = useState<SpecificationMeasureUnit>(
    (defaultValue != null
      ? Array.isArray(defaultValue)
        ? defaultValue[0]?.measureUnit
        : defaultValue.measureUnit
      : null) ??
      field.defaultMeasureUnit ??
      field.allowedMeasureUnits[0] ??
      SpecificationMeasureUnit.NONE
  );

  const [selectedValue, setSelectedValue] = useState<IntervalValue>(defaultValue ?? (field.multiselect ? [] : null));
  const selectedOptionsValue = useMemo(() => toIntervalOptionValue(selectedValue), [selectedValue]);

  const [customValues, setCustomValues] = useState<
    Partial<Record<SpecificationMeasureUnit, MeasureUnitIntervalValue[]>>
  >(() => {
    if (defaultValue == null) {
      return {};
    }

    if (field.multiselect) {
      if (Array.isArray(defaultValue)) {
        // Check that default value is not in options already
        return defaultValue.reduce(
          (acc, curr) => {
            if (
              field.showOptions &&
              field.options?.[curr.measureUnit]?.some((optionItem) =>
                areMeasureUnitIntervalValueAndOptionEqual(curr, optionItem)
              )
            ) {
              return acc;
            }

            acc[curr.measureUnit] = [curr, ...(acc[curr.measureUnit] ?? [])];

            return acc;
          },
          {} as Partial<Record<SpecificationMeasureUnit, MeasureUnitIntervalValue[]>>
        );
      }

      // Check that default value is not in options already
      if (
        field.showOptions &&
        field.options?.[defaultValue.measureUnit]?.some((optionItem) =>
          areMeasureUnitIntervalValueAndOptionEqual(defaultValue, optionItem)
        )
      ) {
        return {};
      }

      return {
        [defaultValue.measureUnit]: [defaultValue],
      };
    }

    if (Array.isArray(defaultValue)) {
      const itemsNotInOptions = defaultValue.filter(
        (defaultValueItem) =>
          // Check that default value is not in options already
          !field.showOptions ||
          !field.options?.[defaultValueItem.measureUnit]?.some((optionItem) =>
            areMeasureUnitIntervalValueAndOptionEqual(defaultValueItem, optionItem)
          )
      );

      if (itemsNotInOptions.length === 0) {
        return {};
      }

      return {
        [itemsNotInOptions[0].measureUnit]: itemsNotInOptions,
      };
    }

    // Check that default value is not in options already
    if (
      field.showOptions &&
      field.options?.[defaultValue.measureUnit]?.some((optionItem) =>
        areMeasureUnitIntervalValueAndOptionEqual(defaultValue, optionItem)
      )
    ) {
      return {};
    }

    return {
      [defaultValue.measureUnit]: [defaultValue],
    };
  });

  const [forceHideDropdown, setForceHideDropdown] = useState<boolean | undefined>(undefined);

  const allowedMeasureUnits: SpecificationMeasureUnit[] = useMemo(
    () =>
      field.allowedMeasureUnits.length > 0
        ? field.allowedMeasureUnits
        : (Object.keys(SpecificationMeasureUnit) as SpecificationMeasureUnit[]),
    [field.allowedMeasureUnits]
  );

  const measureUnitsOptions: DefaultOptionType[] = useMemo(
    () =>
      allowedMeasureUnits.map((measureUnit) => ({
        label: SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[measureUnit],
        value: measureUnit,
      })),
    [allowedMeasureUnits]
  );

  const fieldOptions: DefaultOptionType[] = useMemo(
    () =>
      field.multiselect
        ? // For multiselect show options for all allowed measure units (groupped by measure unit)
          allowedMeasureUnits.map((allowedMeasureUnit) => ({
            label: `${SPECIFICATION_MEASURE_UNIT_MAPPER[allowedMeasureUnit]}`,
            options: [
              // Collect custom values
              ...(customValues[allowedMeasureUnit]?.map(toCustomIntervalDefaultType) ?? []),

              // Collect existed field values
              ...((field.showOptions ? field.options?.[allowedMeasureUnit]?.map(toIntervalDefaultType) : null) ?? []),
            ],
          }))
        : [
            // Collect custom values
            ...(customValues[selectedMeasureUnit]?.map(toCustomIntervalDefaultType) ?? []),

            // Collect existed field values
            ...((field.showOptions ? field.options?.[selectedMeasureUnit]?.map(toIntervalDefaultType) : null) ?? []),
          ],
    [field.multiselect, allowedMeasureUnits, selectedMeasureUnit, customValues, field.showOptions, field.options]
  );

  const handleCustomValueAdd = useCallback(
    (newCustomValues: MeasureUnitIntervalValue[]) => {
      if (newCustomValues.length === 0) {
        return;
      }

      setCustomValues((prev) => {
        return newCustomValues.reduce(
          (acc, curr) => {
            acc[curr.measureUnit] = [curr, ...(acc[curr.measureUnit] ?? [])];

            return acc;
          },
          // Refresh object to cause hook updates
          { ...prev }
        );
      });

      // Automatically select newly added value
      setSelectedValue((prev) => {
        const newSelectedValues = field.multiselect
          ? Array.isArray(prev)
            ? [...prev, ...newCustomValues]
            : newCustomValues
          : (newCustomValues[0] ?? null);

        // ToDo: bad pattern to do it in setState function
        setTimeout(() => onChange?.(newSelectedValues));

        return newSelectedValues;
      });
    },
    [field.multiselect, onChange]
  );

  const handlePopupClose = useCallback(() => {
    setForceHideDropdown(undefined);
  }, []);

  const handleCustomClick = useCallback(() => {
    setForceHideDropdown(true);

    specificationMultiselectCascaderPopupRef.current?.open();
  }, []);

  const handleChange = useCallback(
    (_newValue: IntervalOptionValue, selectedOptions?: DefaultOptionType | DefaultOptionType[] | null) => {
      const newSelectedValues = toIntervalValue(selectedOptions);

      setSelectedValue(newSelectedValues);

      onChange?.(newSelectedValues);
    },
    [onChange]
  );

  const handleMeasureUnitsChange = useCallback(
    (newMeasureUnits: SpecificationMeasureUnit) => {
      setSelectedMeasureUnit(newMeasureUnits);

      setSelectedValue(field.multiselect ? [] : null);

      onChange?.(field.multiselect ? [] : null);
    },
    [field.multiselect, onChange]
  );

  return (
    <div className={cNs('specification-interval-multiselect__container', containerClassName)}>
      {field.name ? <div className="specification-interval-multiselect__label">{field.name}</div> : null}
      <div className="specification-interval-multiselect__container__wrapper">
        <SpecificationIntervalPopup
          ref={specificationMultiselectCascaderPopupRef}
          multiselect={field.multiselect ?? false}
          defaultMeasureUnit={field.multiselect ? (field.defaultMeasureUnit ?? null) : selectedMeasureUnit}
          allowedMeasureUnits={
            field.multiselect
              ? allowedMeasureUnits
              : selectedMeasureUnit !== SpecificationMeasureUnit.NONE
                ? [selectedMeasureUnit]
                : []
          }
          minIntValue={field.minIntValue ?? null}
          maxIntValue={field.maxIntValue ?? null}
          onAddValue={handleCustomValueAdd}
          onClose={handlePopupClose}
        >
          <Select<IntervalOptionValue>
            className={cNs('specification-interval-multiselect__select', className)}
            showSearch={true}
            open={forceHideDropdown === true ? false : undefined}
            value={selectedOptionsValue}
            options={fieldOptions}
            onChange={handleChange}
            mode={field.multiselect ? 'multiple' : undefined}
            status={status}
            disabled={disabled}
            dropdownRender={(menu) => (
              <div className="specification-interval-multiselect__dropdown__container">
                <div className="specification-interval-multiselect__header">Min Value - Max Value</div>

                {menu}

                {field.allowCustomValues && (
                  <div className="specification-interval-multiselect__add-button__container">
                    <Button type="primary" onClick={handleCustomClick}>
                      Add Custom Value
                    </Button>
                  </div>
                )}
              </div>
            )}
            optionRender={(option) =>
              option.data.isCustom ? (
                <div className="specification-interval-multiselect__custom-option">{option.label}</div>
              ) : (
                option.label
              )
            }
          />
        </SpecificationIntervalPopup>

        {!field.multiselect && !noMeasureUnitsAllowed && (
          <Select
            style={{ minWidth: 75 }}
            value={selectedMeasureUnit}
            options={measureUnitsOptions}
            onChange={handleMeasureUnitsChange}
            status={status}
            disabled={disabled}
            popupMatchSelectWidth={false}
            optionRender={({ value }) => <OptionRender value={value} />}
          />
        )}
      </div>
    </div>
  );
}
